import React, { useState } from 'react';
import { motion } from 'framer-motion';
import useTranslation from '../hooks/useTranslation';
import './Contact.css';

const Contact = () => {
  const { t, isRTL } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    // TODO: Replace with real API call when backend is ready.
    // Example:
    // const res = await fetch('https://api.orkina.com/contact', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(form),
    // });
    // const data = await res.json();

    await new Promise((r) => setTimeout(r, 1400)); // Simulated delay
    setSending(false);
    setSent(true);
    setForm({ name: '', email: '', message: '' });
    setTimeout(() => setSent(false), 6000);
  };

  return (
    <section id="contact" className="contact" aria-label={isRTL ? 'تواصل معنا' : 'Contact Us'}>
      <div className="contact__bg-shape" aria-hidden="true" />
      <div className="container">
        <motion.div
          className="contact__inner"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="contact__header">
            <div className="contact__label" aria-hidden="true">
              <span />
              <span>{isRTL ? 'ابدأ الحوار' : 'Start the Conversation'}</span>
            </div>
            <h2 className="contact__title">{t('contact.title')}</h2>
            <p className="contact__subtitle">{t('contact.subtitle')}</p>
          </div>

          <div className="contact__layout">
            {/* Contact Details */}
            <div className="contact__info">
              <div className="contact__info-card">
                <div className="contact__info-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                  </svg>
                </div>
                <div>
                  <h4>{isRTL ? 'اتصل بنا' : 'Call Us'}</h4>
                  <p>0567114651</p>
                </div>
              </div>

              <div className="contact__info-card">
                <div className="contact__info-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <div>
                  <h4>{isRTL ? 'البريد الإلكتروني' : 'Email Us'}</h4>
                  <p>info@orkina.com</p>
                </div>
              </div>

              <div className="contact__info-card">
                <div className="contact__info-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <h4>{isRTL ? 'موقعنا' : 'Our Location'}</h4>
                  <p>{isRTL ? 'دبي، الإمارات العربية المتحدة' : 'Dubai, UAE'}</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form
              className="contact__form"
              onSubmit={handleSubmit}
              noValidate
              aria-label={isRTL ? 'نموذج التواصل' : 'Contact form'}
            >
              <div className="contact__row">
                <div className="contact__field">
                  <label htmlFor="contact-name" className="contact__field-label">
                    {t('contact.name')}
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    className="contact__input"
                    placeholder={t('contact.name')}
                    value={form.name}
                    onChange={handleChange}
                    required
                    autoComplete="name"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>
                <div className="contact__field">
                  <label htmlFor="contact-email" className="contact__field-label">
                    {t('contact.email')}
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    className="contact__input"
                    placeholder={t('contact.email')}
                    value={form.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    dir="ltr"
                  />
                </div>
              </div>

              <div className="contact__field">
                <label htmlFor="contact-message" className="contact__field-label">
                  {t('contact.message')}
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  className="contact__input contact__textarea"
                  placeholder={t('contact.message')}
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>

              {sent && (
                <motion.div
                  className="contact__success"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  role="status"
                  aria-live="polite"
                >
                  ✅ {t('contact.success')}
                </motion.div>
              )}

              <motion.button
                type="submit"
                className="btn-accent contact__submit"
                disabled={sending}
                whileHover={!sending ? { y: -3, boxShadow: '0 10px 36px rgba(200,168,107,0.5)' } : {}}
                whileTap={!sending ? { scale: 0.97 } : {}}
                id="contact-submit-btn"
              >
                {sending ? (
                  <>
                    <span className="contact__spinner" aria-hidden="true" />
                    {t('contact.sending')}
                  </>
                ) : (
                  t('contact.send')
                )}
              </motion.button>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
